import React from "react";
import { Row, Col, Card, CardBody } from "reactstrap";
import { motion } from "framer-motion";
import { FaBookOpen, FaGlobe, FaLanguage, FaMagic } from "react-icons/fa";

const genres = [
  {
    genreName: "Indian Languages",
    icon: <FaLanguage size={26} />,
    description: "Rich literary heritage spanning classical and contemporary regional Indian languages.",
  },
  {
    genreName: "Fantasy",
    icon: <FaMagic size={24} />,
    description: "Immerse yourself in legendary worldbuilding, epic lore, and mystical adventures.",
  },
  {
    genreName: "Global Literature",
    icon: <FaGlobe size={24} />,
    description: "Broaden your horizons with prize-winning contemporary fiction from around the world.",
  },
  {
    genreName: "Science Fiction",
    icon: <FaBookOpen size={24} />,
    description: "Journey into complex future technologies, space exploration, and deep scientific queries.",
  },
];

const GenreGrid = () => {
  return (
    <div className="modern-genre-showcase py-4">
      {/* Scoped style definitions capturing modern SaaS UI layouts */}
      <style>{`
        :root {
          --slate-title: #0f172a;
          --slate-desc: #64748b;
          --accent-primary: #6366f1;
          --accent-soft: rgba(99, 102, 241, 0.06);
          --card-radius: 20px;
          --smooth-curve: cubic-bezier(0.16, 1, 0.3, 1);
        }

        .genre-section-header {
          margin-bottom: 48px;
        }

        .genre-badge {
          display: inline-block;
          padding: 6px 12px;
          background: var(--accent-soft);
          color: var(--accent-primary);
          font-weight: 600;
          font-size: 0.85rem;
          border-radius: 100px;
          margin-bottom: 12px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .genre-main-heading {
          font-weight: 800;
          color: var(--slate-title);
          letter-spacing: -1px;
        }

        .modern-genre-card {
          border: 1px solid rgba(0, 0, 0, 0.04) !important;
          border-radius: var(--card-radius) !important;
          background: #ffffff !important;
          box-shadow: 0 10px 30px rgba(15, 23, 42, 0.02);
          transition: all 0.4s var(--smooth-curve) !important;
          cursor: pointer;
        }

        .genre-icon-circle {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          background-color: var(--accent-soft);
          color: var(--accent-primary);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 24px;
          transition: all 0.4s var(--smooth-curve);
        }

        .genre-card-name {
          font-weight: 700;
          color: var(--slate-title);
          font-size: 1.2rem;
          margin-bottom: 12px;
          letter-spacing: -0.3px;
        }

        .genre-card-desc {
          color: var(--slate-desc);
          font-size: 0.95rem;
          line-height: 1.6;
          margin-bottom: 0;
        }

        /* High-End Hover Orchestration styles */
        .modern-genre-card:hover {
          transform: translateY(-6px);
          border-color: rgba(99, 102, 241, 0.25) !important;
          box-shadow: 0 20px 40px rgba(99, 102, 241, 0.07) !important;
        }

        .modern-genre-card:hover .genre-icon-circle {
          background-color: var(--accent-primary);
          color: #ffffff;
          transform: scale(1.05);
          box-shadow: 0 8px 20px rgba(99, 102, 241, 0.25);
        }
      `}</style>

      {/* Grid Content Layout */}
      <div className="genre-section-header text-center">
        <span className="genre-badge">Curated Catalogs</span>
        <h2 className="genre-main-heading h1">Explore Top Genres</h2>
        <p className="text-muted fs-5">Dive into structured universes hand-picked for absolute reading pleasure.</p>
      </div>

      <Row className="gy-4 justify-content-center">
        {genres.map((genre, index) => (
          <Col lg="3" md="6" key={index} className="px-3">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
              className="h-100"
            >
              <Card className="h-100 modern-genre-card p-4">
                <CardBody className="p-0 d-flex flex-column align-items-start text-start">
                  <div className="genre-icon-circle">
                    {genre.icon}
                  </div>
                  <h3 className="genre-card-name">{genre.genreName}</h3>
                  <p className="genre-card-desc">{genre.description}</p>
                </CardBody>
              </Card>
            </motion.div>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default GenreGrid;